import { Controller } from "@hotwired/stimulus"

// The donation landing page's "<year> Donations" row (TMS-1796). When the
// cards are wider than the row, a copy of them is appended and CSS slides the
// pair right to left, one copy's width per loop, so the row never runs out.
// The CSS pauses it while the row is hovered or focused. Under reduced motion,
// or when every card already fits, nothing is copied and the row stays still
// (scrollable by hand if it overflows).
const PIXELS_PER_SECOND = 40

export default class extends Controller {
  static targets = ["track"]

  connect() {
    this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)")
    this.originals = Array.from(this.trackTarget.children)
    this.update = this.update.bind(this)
    this.reduceMotion.addEventListener("change", this.update)
    this.resizeObserver = new ResizeObserver(this.update)
    this.resizeObserver.observe(this.element)
    this.update()
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    this.reduceMotion.removeEventListener("change", this.update)
    this.stop()
  }

  update() {
    const wanted = !this.reduceMotion.matches && this.originalWidth() > this.element.clientWidth
    if (wanted === this.scrolling) return

    if (wanted) {
      this.start()
    } else {
      this.stop()
    }
  }

  start() {
    this.originals.forEach((card) => {
      const copy = card.cloneNode(true)
      copy.setAttribute("aria-hidden", "true")
      copy.dataset.copy = ""
      this.trackTarget.appendChild(copy)
    })
    const distance = this.originalWidth()
    this.trackTarget.style.setProperty("--dl-feed-distance", `-${distance}px`)
    this.trackTarget.style.setProperty("--dl-feed-duration", `${Math.round(distance / PIXELS_PER_SECOND)}s`)
    this.element.classList.add("is-scrolling")
    this.scrolling = true
  }

  stop() {
    this.trackTarget.querySelectorAll("[data-copy]").forEach((copy) => copy.remove())
    this.element.classList.remove("is-scrolling")
    this.scrolling = false
  }

  // The originals' run, gap included, which is how far one loop travels.
  originalWidth() {
    const first = this.originals[0]
    const last = this.originals[this.originals.length - 1]
    const gap = parseFloat(getComputedStyle(this.trackTarget).columnGap) || 0
    return last.offsetLeft + last.offsetWidth - first.offsetLeft + gap
  }
}
